import { useId } from "react";
import AddressFields, { AvisoTablas, CountrySelect, Etiqueta } from "@/components/registry/AddressFields";
import { conTipoDocumento, direccionVacia } from "@/lib/registro";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "mt-1 bg-white border-[#DCD8D0]";

function Choice({ value, onChange, options, testid, placeholder, id, required = false }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger id={id} className={inputCls} data-testid={testid} aria-required={required || undefined}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

// Una persona para el registro de viajeros (titular o segundo conductor). Con los Mossos no se ofrece «Otro» en el
// sexo: su esquema solo admite hombre o mujer (pregunta 13 de la especificación). Cada etiqueta va asociada a su
// campo con un id propio de esta instancia (puede haber dos personas en la misma página).
// `autoCompletar={false}` cuando no es quien escribe (el segundo conductor): el navegador no propone sus datos.
// `avisarTablas={false}` cuando la página ya enseña `AvisoTablas` una vez para todas sus personas.
// `basico` (sin canal: el registro no se lleva) pide solo lo de siempre: nombre y apellidos, documento, nacimiento,
// dirección, número del carné, teléfono y correo; al segundo conductor (`titular={false}`), sin dirección, teléfono
// ni correo, que solo usa el registro. Lo demás solo sirve al registro (minimización).
// `obligatorios` (`obligatorios()` de lib/registro) marca con «*» los campos que se exigen.
export default function PersonFields({ value, onChange, refdata, canal, testid = "person", autoCompletar = true,
  avisarTablas = true, basico = false, titular = true, obligatorios }) {
  const contacto = !basico || titular; // dirección, teléfono y correo
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  const auto = (token) => (autoCompletar ? token : "off");
  const req = (k) => Boolean(obligatorios?.has(k));
  const reqDireccion = obligatorios
    ? new Set([...obligatorios].filter((k) => k.startsWith("address.")).map((k) => k.slice("address.".length)))
    : undefined;
  const p = value || {};
  const lic = p.license || {};
  const set = (k, v) => onChange({ ...p, [k]: v });
  const setLic = (k, v) => onChange({ ...p, license: { ...lic, [k]: v } });
  const sexos = (refdata?.sexes || []).filter((s) => canal !== "mossos" || s.code !== "O");
  const clases = (refdata?.license_classes || []).map((c) => ({ code: c, label: c }));
  // Al cambiar el tipo, los valores por defecto que ya no encajan se vacían (`conTipoDocumento`). Con DNI o NIE el
  // país del documento es España y no se cambia.
  const setDocType = (v) => onChange(conTipoDocumento(p, v));
  const paisFijo = p.doc_type === "NIF" || p.doc_type === "NIE";
  const texto = (k, label, extra = {}) => (
    <div>
      <Etiqueta htmlFor={id(k)} obligatorio={req(k)}>{label}</Etiqueta>
      <Input id={id(k)} className={inputCls} value={p[k] || ""} onChange={(e) => set(k, e.target.value)}
        data-testid={`${testid}-${k.replace(/_/g, "-")}`} {...extra} autoComplete={auto(extra.autoComplete)}
        aria-required={req(k) || undefined} />
    </div>
  );
  const numeroDocumento = (
    <div><Etiqueta htmlFor={id("doc_number")} obligatorio={req("doc_number")}>
      {basico ? "Número de documento (DNI, NIE o pasaporte)" : "Número del documento"}</Etiqueta>
      <Input id={id("doc_number")} className={inputCls} value={p.doc_number || ""} autoComplete={auto(undefined)}
        aria-required={req("doc_number") || undefined}
        onChange={(e) => set("doc_number", e.target.value.toUpperCase())} data-testid={`${testid}-doc-number`} /></div>
  );
  const nacimiento = texto("birth_date", "Fecha de nacimiento", { type: "date", autoComplete: "bday" });
  const numeroCarne = (
    <div><Etiqueta htmlFor={id("license_number")} obligatorio={req("license.number")}>Número del carné</Etiqueta>
      <Input id={id("license_number")} className={inputCls} value={lic.number || ""} autoComplete={auto(undefined)}
        aria-required={req("license.number") || undefined}
        onChange={(e) => setLic("number", e.target.value.toUpperCase())} data-testid={`${testid}-license-number`} /></div>
  );
  return (
    <div className="space-y-4" data-testid={testid}>
      {avisarTablas && <AvisoTablas refdata={refdata} />}
      {obligatorios?.size > 0 && <p className="text-xs text-[#5C5C5C]">Los campos marcados con * son obligatorios.</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        {texto("first_name", "Nombre", { autoComplete: "given-name" })}
        {texto("last_name1", "Primer apellido", { autoComplete: "family-name" })}
        {texto("last_name2", "Segundo apellido")}
      </div>
      {basico ? (
        <div className="grid sm:grid-cols-2 gap-3">{numeroDocumento}{nacimiento}</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Etiqueta htmlFor={id("sex")} obligatorio={req("sex")}>Sexo (como en el documento)</Etiqueta>
              <Choice id={id("sex")} value={p.sex} onChange={(v) => set("sex", v)} options={sexos} testid={`${testid}-sex`}
                placeholder="Elige" required={req("sex")} /></div>
            <div><Etiqueta htmlFor={id("nationality")} obligatorio={req("nationality")}>Nacionalidad</Etiqueta>
              <CountrySelect id={id("nationality")} value={p.nationality} onChange={(v) => set("nationality", v)} refdata={refdata}
                testid={`${testid}-nationality`} required={req("nationality")} /></div>
            {nacimiento}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Etiqueta htmlFor={id("doc_type")} obligatorio={req("doc_type")}>Tipo de documento</Etiqueta>
              <Choice id={id("doc_type")} value={p.doc_type} onChange={setDocType} options={refdata?.doc_types || []}
                testid={`${testid}-doc-type`} placeholder="Elige" required={req("doc_type")} /></div>
            <div><Etiqueta htmlFor={id("doc_country")} obligatorio={req("doc_country")}>País del documento</Etiqueta>
              <CountrySelect id={id("doc_country")} value={p.doc_country} onChange={(v) => set("doc_country", v)} refdata={refdata}
                testid={`${testid}-doc-country`} required={req("doc_country")} disabled={paisFijo} /></div>
            {numeroDocumento}
          </div>
        </>
      )}
      {/* min-w-0: un fieldset no encoge por debajo de su contenido y rompería la rejilla en el móvil. */}
      {contacto && (
        <fieldset className="min-w-0">
          <legend className="text-sm font-medium text-[#1A1A1A] mb-1">Dirección</legend>
          <AddressFields value={p.address || direccionVacia()} onChange={(a) => set("address", a)} refdata={refdata}
            testid={`${testid}-addr`} avisarTablas={false} autoCompletar={autoCompletar} obligatorios={reqDireccion} />
        </fieldset>
      )}
      {basico ? (
        <div className="grid sm:grid-cols-2 gap-3">{numeroCarne}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Etiqueta htmlFor={id("license_class")} obligatorio={req("license.license_class")}>Clase de carné</Etiqueta>
            <Choice id={id("license_class")} value={lic.license_class} onChange={(v) => setLic("license_class", v)} options={clases}
              testid={`${testid}-license-class`} placeholder="Elige" required={req("license.license_class")} /></div>
          {numeroCarne}
          <div><Etiqueta htmlFor={id("license_expiry")} obligatorio={req("license.expiry")}>Caducidad del carné</Etiqueta>
            <Input id={id("license_expiry")} type="date" className={inputCls} value={lic.expiry || ""} autoComplete={auto(undefined)}
              aria-required={req("license.expiry") || undefined}
              onChange={(e) => setLic("expiry", e.target.value)} data-testid={`${testid}-license-expiry`} /></div>
          <div><Etiqueta htmlFor={id("license_country")} obligatorio={req("license.country")}>País del carné</Etiqueta>
            <CountrySelect id={id("license_country")} value={lic.country} onChange={(v) => setLic("country", v)} refdata={refdata}
              testid={`${testid}-license-country`} required={req("license.country")} /></div>
        </div>
      )}
      {contacto && (
        <div className="grid sm:grid-cols-2 gap-3">
          {texto("phone", "Teléfono (con prefijo, p. ej. +34 600 000 000)", { type: "tel", inputMode: "tel", autoComplete: "tel" })}
          {texto("email", "Correo electrónico", { type: "email", autoComplete: "email" })}
        </div>
      )}
    </div>
  );
}
